import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Sparkles, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { WARNING_SIGNS, IMAGES } from "../lib/content";

const SITUATIONS = ["My partner", "My child", "My friend", "My parent", "Myself"];
const GUIDE = [
  ["Starting the kōrero", "Pick a calm, sober moment — never mid-crisis. Lead with aroha, not accusation: 'I've noticed…, and I'm worried about you' lands where labels never will. Ask questions and listen. Expect denial; stay in the relationship, not the argument."],
  ["What doesn't work", "Ultimatums given in anger, searching and seizing (it escalates danger), covering up and paying debts (it feeds the cycle), and silence. Silence protects the pipe, not the person."],
  ["Looking after yourself", "Whānau need support too — this is not selfishness, it's strategy. The Alcohol & Drug Helpline (0800 787 797) supports family members, not just users. Set boundaries you can keep, and keep the kids' routines steady."],
  ["Pathways in Aotearoa", "Start with the free 24/7 lines: 1737 (call or text) for mental health support, and 0800 787 797 for alcohol and drug concerns — with dedicated lines for Māori (0800 787 798), Pasifika (0800 787 799) and young people (0800 787 984). Your GP can refer to local CADS or community addiction services. In an emergency — violence, overdose, psychosis — call 111."],
];

export default function WhanauGuide() {
  const [openIdx, setOpenIdx] = useState(0);
  const [situation, setSituation] = useState(null);
  const [starters, setStarters] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async (s) => {
    setSituation(s);
    setBusy(true);
    setStarters("");
    try {
      const { data } = await api.post("/korero", { situation: s });
      setStarters(data.starters);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Kōrero starters are busy — please try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="whanau" className="bg-[#F5F2EC] text-[#1A1A1A] py-24 sm:py-32 border-t border-[#E2DCD2]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-14">
          <div className="lg:col-span-3">
            <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mono-label text-[#C2410C] mb-4">For whānau · early intervention</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display font-extrabold uppercase text-3xl sm:text-5xl tracking-tight mb-6">
              See it early.<br />Say it with aroha.
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-[#52525B] mb-8 max-w-xl">
              You don't need to wait for crisis. If someone you love is changing, these are the signs families across Aotearoa describe first.
            </motion.p>

            <div data-testid="whanau-warning-signs-accordion" className="space-y-3 mb-10">
              <div className="bg-white border border-[#E2DCD2] rounded-xl p-5">
                <h4 className="font-display font-bold uppercase text-sm tracking-wide mb-3 text-[#C2410C]">Warning signs</h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  {WARNING_SIGNS.map(([name, desc]) => (
                    <div key={name}>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#1E4B3E] mb-1">{name}</p>
                      <p className="text-xs text-[#52525B] leading-relaxed">{desc}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#52525B] italic mt-4 pt-3 border-t border-[#E2DCD2]">These changes don't always mean meth use — stress, grief and mental health can look the same. Lead with curiosity and care, not conclusions.</p>
              </div>
              {GUIDE.map(([title, body], i) => (
                <div key={title} className="bg-white border border-[#E2DCD2] rounded-xl overflow-hidden">
                  <button data-testid={`whanau-accordion-${i}`} onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <span className="font-display font-bold text-sm uppercase tracking-wide">{title}</span>
                    <ChevronDown size={16} className={`transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
                  </button>
                  {openIdx === i && <p className="px-5 pb-5 text-sm text-[#52525B] leading-relaxed">{body}</p>}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="clipped-frame overflow-hidden">
              <img src={IMAGES.portrait1} alt="Whānau together, hopeful" className="w-full h-56 object-cover" />
            </div>
            <div className="bg-[#1E4B3E] text-[#F5F2EC] rounded-2xl p-7" data-testid="korero-starter-box">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={18} className="text-[#F59E0B]" />
                <h3 className="font-display font-bold uppercase text-sm tracking-wide">Kōrero starters</h3>
              </div>
              <p className="text-xs opacity-80 mb-5">Not sure how to open the conversation? Choose who it's with — we'll suggest gentle first words you can make your own.</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {SITUATIONS.map((s) => (
                  <button key={s} data-testid={`korero-${s.toLowerCase().replace(/\s/g, "-")}`} onClick={() => generate(s)} disabled={busy}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold transition-all ${situation === s ? "bg-[#F59E0B] text-black" : "bg-white/10 hover:bg-white/20"}`}>
                    {s}
                  </button>
                ))}
              </div>
              {busy && <p className="text-xs opacity-70 animate-pulse">Finding the words…</p>}
              {starters && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-black/20 rounded-xl p-4 text-sm leading-relaxed whitespace-pre-line" data-testid="korero-result">
                  {starters}
                </motion.div>
              )}
            </div>
            <div className="bg-[#DC2626] text-white rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2"><Phone size={16} /><h3 className="font-display font-bold uppercase text-sm tracking-wide">Right now</h3></div>
              <p className="text-sm opacity-90 leading-relaxed">Worried about someone tonight? Free call or text <a href="tel:1737" className="font-bold underline">1737</a> · Alcohol &amp; Drug Helpline <a href="tel:0800787797" className="font-bold underline">0800 787 797</a> (Māori <a href="tel:0800787798" className="font-bold underline">0800 787 798</a> · Pasifika <a href="tel:0800787799" className="font-bold underline">0800 787 799</a> · Youth <a href="tel:0800787984" className="font-bold underline">0800 787 984</a>) · Emergency <a href="tel:111" className="font-bold underline">111</a></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
